import {
  Divider,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Stack,
  Typography,
} from '@mui/material';
import {
  type RestoreJob,
  Provider,
  type RestoreAzureDiskConfig,
  type RestoreAzureDiskRequest,
  type RestoreEbsVolumeConfig,
  type RestoreEksNamespaceVolumeInput,
  type RestoreGceDiskConfig,
  type RestoreGcpInstanceDiskInput,
  type RestoreInstanceVolumeInput,
} from '@repo/api-gw-sdk';

import { MultiAccordionsItem } from '@/components/accordion/multiAccordionsItem';
import { SidePanelPropsTable } from '@/components/panels/instance/tabs/sidePanelPropsTable';
import { Tags } from '@/components/tags';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

export const VolumeConfigurationSection = ({
  job,
  cloudProvider,
}: {
  job: RestoreJob;
  cloudProvider: Provider;
}) => {
  const volumes:
    | RestoreEbsVolumeConfig[]
    | RestoreAzureDiskConfig[]
    | RestoreGceDiskConfig[]
    | RestoreAzureDiskRequest[]
    | RestoreGcpInstanceDiskInput[]
    | RestoreEksNamespaceVolumeInput[]
    | RestoreInstanceVolumeInput[] = job.configuration?.restoreEbsVolume
    ? [job.configuration.restoreEbsVolume]
    : job.configuration?.restoreAzureDisk
      ? [job.configuration.restoreAzureDisk]
      : job.configuration?.restoreGceDisk
        ? [job.configuration.restoreGceDisk]
        : job.configuration?.restoreAzureVMInstance?.diskSettings
          ? job.configuration.restoreAzureVMInstance.diskSettings
          : job.configuration?.restoreGceInstance?.diskSettings
            ? job.configuration.restoreGceInstance.diskSettings
            : job.configuration?.restoreEksNamespace?.volumeSettings
              ? job.configuration.restoreEksNamespace.volumeSettings
              : job.configuration?.restoreEc2Instance?.volumeRestoreParameters
                ? job.configuration.restoreEc2Instance.volumeRestoreParameters
                : [];
  const title =
    cloudProvider === Provider.Aws
      ? 'Volumes configuration'
      : 'Disks configuration';

  return (
    volumes.length > 0 && (
      <MuiAccordion disableGutters defaultExpanded>
        <MuiAccordionSummary data-testid='operation-summary'>
          {title}
        </MuiAccordionSummary>
        <MuiAccordionDetails sx={{ padding: '24px 0' }}>
          <Stack divider={<Divider />} gap='24px'>
            {volumes?.map((volume, idx) => {
              const volId: string =
                'providerVolumeId' in volume
                  ? volume.providerVolumeId
                  : 'diskId' in volume
                    ? (volume.diskId as string)
                    : `Volume ${idx + 1}`;
              const isMulti = volumes.length > 1;
              return (
                <MultiAccordionsItem
                  key={idx}
                  defaultExpanded={idx === 0}
                  isMulti={isMulti}
                  Summary={isMulti ? <Typography>{volId}</Typography> : null}
                  Details={
                    <VolumeConfiguration
                      volume={volume}
                      cloudProvider={cloudProvider}
                    />
                  }
                />
              );
            })}
          </Stack>
        </MuiAccordionDetails>
      </MuiAccordion>
    )
  );
};

const VolumeConfiguration = ({
  volume,
  cloudProvider,
}: {
  volume:
    | RestoreEbsVolumeConfig
    | RestoreAzureDiskConfig
    | RestoreGceDiskConfig
    | RestoreAzureDiskRequest
    | RestoreGcpInstanceDiskInput
    | RestoreEksNamespaceVolumeInput
    | RestoreInstanceVolumeInput;
  cloudProvider: Provider;
}) => {
  const volumeSettings =
    'volumeSettings' in volume ? volume.volumeSettings : undefined;
  const diskSettings = 'settings' in volume ? volume.settings : undefined;
  const diskName = diskSettings?.name || undefined;
  const encryptionKeyId =
    'encryptionKeyId' in volume ? volume.encryptionKeyId : undefined;
  const tags =
    'labels' in volume ? volume.labels : 'tags' in volume ? volume.tags : {};
  const tagsTitle = cloudProvider === Provider.Gcp ? 'Labels' : 'Tags';
  const throughput =
    volumeSettings?.throughput ||
    ('throughput' in volume ? volume.throughput : undefined);
  const iops =
    volumeSettings?.iops || ('iops' in volume ? volume.iops : undefined);
  const sizeBytes =
    volumeSettings?.sizeBytes ||
    ('diskSizeBytes' in volume ? volume.diskSizeBytes : undefined);
  const type =
    volumeSettings?.type ||
    ('diskType' in volume ? volume.diskType : undefined);
  const tier = 'diskTier' in volume ? volume.diskTier : undefined;
  const hyperVGeneration =
    'diskHyperVGeneration' in volume ? volume.diskHyperVGeneration : undefined;
  const zone =
    'zone' in volume
      ? volume.zone
      : 'availabilityZone' in volume
        ? volume.availabilityZone
        : undefined;
  const zoneTitle =
    cloudProvider === Provider.Gcp ? 'Zone' : 'Availability Zone';

  return (
    <SidePanelPropsTable withDividers columnSpace='medium'>
      <tbody>
        <>
          {diskName && (
            <tr key='diskName'>
              <td className='props-col'>{'Disk Name'}</td>
              <td className='break-all'>{diskName}</td>
            </tr>
          )}
          {zone && (
            <tr key='zone'>
              <td className='props-col'>{zoneTitle}</td>
              <td className='break-all'>{zone}</td>
            </tr>
          )}
          {encryptionKeyId && (
            <tr key='encryptionKeyId'>
              <td className='props-col'>{'Encryption Key'}</td>
              <td className='break-all'>{encryptionKeyId}</td>
            </tr>
          )}
          {tags && (
            <tr key='tags'>
              <td className='props-col'>{tagsTitle}</td>
              <td className='break-all'>{<Tags tags={tags} />}</td>
            </tr>
          )}
          {iops && (
            <tr key='iops'>
              <td className='props-col'>{'IOPS'}</td>
              <td className='break-all'>{iops}</td>
            </tr>
          )}
          {throughput && (
            <tr key='throughput'>
              <td className='props-col'>{'Throughput'}</td>
              <td className='break-all'>{throughput}</td>
            </tr>
          )}
          {sizeBytes && (
            <tr key='sizeBytes'>
              <td className='props-col'>{'Size'}</td>
              <td className='break-all'>
                {fileSizeFormatter(sizeBytes, 1, false)}
              </td>
            </tr>
          )}
          {type && (
            <tr key='type'>
              <td className='props-col'>{'Type'}</td>
              <td className='break-all'>{type}</td>
            </tr>
          )}
          {tier && (
            <tr key='tier'>
              <td className='props-col'>{'Disk Tier'}</td>
              <td className='break-all'>{tier}</td>
            </tr>
          )}
          {hyperVGeneration && (
            <tr key='hyperVGeneration'>
              <td className='props-col'>{'Hyper-V Generation'}</td>
              <td className='break-all'>{hyperVGeneration}</td>
            </tr>
          )}
        </>
      </tbody>
    </SidePanelPropsTable>
  );
};
